import React from 'react';
import DropdownField from '@/components/form/DropdownField';

interface TimeDropdownProps {
  id: string;
  name: string;
  label?: string;
  value: Date | string;
  onChange: (time: Date) => void;
  error?: string;
  required?: boolean;
  className?: string;
  startTime?: Date | string; // Reference time for duration calculation
  showDuration?: boolean; // Whether to show duration in options
}

// Generate time options in 15-minute increments with optional duration display
const generateTimeOptions = (startTime?: Date | string, showDuration?: boolean) => {
  const options = [];
  
  // Convert start time to minutes for duration calculation
  let startMinutes = 0;
  if (startTime && showDuration) {
    let startDate: Date;
    if (typeof startTime === 'string') {
      startDate = new Date(startTime);
    } else {
      startDate = startTime;
    }
    
    if (!isNaN(startDate.getTime())) {
      startMinutes = startDate.getHours() * 60 + startDate.getMinutes();
    }
  }
  
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      
      // Convert to 12-hour format for display
      let hour12 = hour;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      
      if (hour === 0) {
        hour12 = 12; // 12 AM
      } else if (hour > 12) {
        hour12 = hour - 12; // 1-11 PM
      }
      // hour12 stays the same for 1-11 AM and 12 PM
      
      const time12 = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
      
      // Calculate duration if enabled
      let label = time12;
      if (showDuration && startTime) {
        const currentMinutes = hour * 60 + minute;
        let durationMinutes = currentMinutes - startMinutes;
        
        // Handle next day scenario (e.g., start at 11 PM, end at 1 AM)
        if (durationMinutes < 0) {
          durationMinutes += 24 * 60; // Add 24 hours in minutes
        }
        
        // Format duration
        const hours = Math.floor(durationMinutes / 60);
        const mins = durationMinutes % 60;
        
        let durationText = '';
        if (hours === 0) {
          durationText = `${mins} mins`;
        } else if (hours === 1 && mins === 0) {
          durationText = '1 hr';
        } else if (mins === 0) {
          durationText = `${hours} hrs`;
        } else {
          durationText = `${hours} hr ${mins} mins`;
        }
        
        label = `${time12} (${durationText})`;
      }
      
      options.push({
        value: time24,
        label: label
      });
    }
  }
  
  return options;
};

// Convert Date object or date string to HH:MM string
const dateToTimeString = (date: Date | string): string => {
  let dateObj: Date;
  
  if (typeof date === 'string') {
    dateObj = new Date(date);
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    // Fallback to current time if invalid
    dateObj = new Date();
  }
  
  // Check if the date is valid
  if (isNaN(dateObj.getTime())) {
    // Fallback to current time if invalid date
    dateObj = new Date();
  }
  
  const hours = dateObj.getHours().toString().padStart(2, '0');
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

// Convert HH:MM string to Date object (preserving the original date)
const timeStringToDate = (timeString: string, originalDate: Date | string): Date => {
  const [hours, minutes] = timeString.split(':').map(Number);
  
  let baseDate: Date;
  if (typeof originalDate === 'string') {
    baseDate = new Date(originalDate);
  } else if (originalDate instanceof Date) {
    baseDate = originalDate;
  } else {
    baseDate = new Date();
  }
  
  // Check if the base date is valid
  if (isNaN(baseDate.getTime())) {
    baseDate = new Date();
  }
  
  const newDate = new Date(baseDate);
  newDate.setHours(hours, minutes, 0, 0);
  return newDate;
};

const TimeDropdown: React.FC<TimeDropdownProps> = ({
  id,
  name,
  label,
  value,
  onChange,
  error,
  required = false,
  className = "",
  startTime,
  showDuration = false
}) => {
  const timeOptions = generateTimeOptions(startTime, showDuration);
  const currentTimeString = dateToTimeString(value);
  

  
  const handleTimeChange = (selectedTime: string) => {
    if (selectedTime) {
      const newDate = timeStringToDate(selectedTime, value);
      onChange(newDate);
    }
  };

  return (
    <div className={className}>
      <DropdownField
        id={id}
        name={name}
        label={label}
        value={currentTimeString}  // ✅ Pass string value for proper highlighting
        onChange={handleTimeChange}
        options={timeOptions}
        placeholder="Select time"
        required={required}
        error={error}
        // Removed high z-index that was causing overlap with Timezone dropdown
        className="relative"
      />
    </div>
  );
};

export default TimeDropdown;
